
import { Plus, Trash2 } from "lucide-react";
import NumberInput from "../input/NumberInput";
import Combobox, { type ComboboxOption } from "../input/Combobox";
import { getProducts } from "../../api/productApi";

export interface DeliveryOrderItemRow {
  description: string;
  qty: number;
  unit: string;
  remark: string;
  product_id?: number | null;
}

interface DeliveryOrderItemTableProps {
  items: DeliveryOrderItemRow[];
  onChange: (items: DeliveryOrderItemRow[]) => void;
}

const EMPTY_ROW: DeliveryOrderItemRow = {
  description: "",
  qty: 1,
  unit: "",
  remark: "",
  product_id: null,
};

// =========================
// Search Product
// =========================

const fetchProductOptions = async (
  search: string
): Promise<ComboboxOption[]> => {
  try {
    const response = await getProducts({
      page: 1,
      limit: 20,
      search,
    });

    const list =
      response?.data?.productList ?? [];

    return list.map(
      (product: {
        id: number;
        productName: string;
        category?: string | null;
      }) => ({
        id: product.id,
        label: product.productName,
        sublabel: product.category ?? undefined,
      })
    );
  } catch (error) {
    console.error("Failed to search products:", error);
    return [];
  }
};

export default function DeliveryOrderItemTable({
  items,
  onChange,
}: DeliveryOrderItemTableProps) {
  const updateRow = <K extends keyof DeliveryOrderItemRow>(
    index: number,
    key: K,
    value: DeliveryOrderItemRow[K]
  ) => {
    const next = [...items];

    next[index] = {
      ...next[index],
      [key]: value,
    };

    onChange(next);
  };

  const addRow = () => {
    onChange([
      ...items,
      {
        ...EMPTY_ROW,
      },
    ]);
  };

  const removeRow = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="pb-2 font-medium">Product</th>

            <th className="w-20 pb-2 font-medium">QTY</th>

            <th className="w-24 pb-2 font-medium">Unit</th>

            <th className="w-48 pb-2 font-medium">Remark</th>

            <th className="w-10 pb-2" />
          </tr>
        </thead>

        <tbody>
          {items.map((item, index) => (
            <tr key={index} className="border-t border-gray-100">
              {/* Product */}
              <td className="py-2 pr-2">
                <Combobox
                  inputValue={item.description}
                  onInputChange={(value) => {
                    const next = [...items];

                    next[index] = {
                      ...next[index],
                      description: value,
                      product_id: null,
                    };

                    onChange(next);
                  }}
                  onSelectOption={(option) => {
                    const next = [...items];

                    next[index] = {
                      ...next[index],
                      description: option.label,
                      product_id: Number(option.id),
                    };

                    onChange(next);
                  }}
                  fetchOptions={fetchProductOptions}
                  placeholder="Search product..."
                  wrapperClassName="w-full"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
                  aria-label="Search product"
                />
              </td>

              {/* QTY */}
              <td className="py-2 pr-2">
                <NumberInput
                  value={item.qty}
                  onChange={(v) => updateRow(index, "qty", v)}
                  required
                  aria-label="Quantity"
                />
              </td>

              {/* Unit */}
              <td className="py-2 pr-2">
                <select
                  value={item.unit}
                  onChange={(e) => updateRow(index, "unit", e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
                  aria-label="Unit">
                  <option value="">Select unit</option>
                  <option value="Pcs">Pcs</option>
                  <option value="Kg">Kg</option>
                  <option value="Gram">Gram</option>
                  <option value="Pack">Pack</option>
                  <option value="Box">Box</option>
                  <option value="Set">Set</option>
                  <option value="Unit">Unit</option>
                  <option value="Liter">Liter</option>
                  <option value="Meter">Meter</option>
                  <option value="Lusin">Lusin</option>
                  <option value="Roll">Roll</option>
                </select>
              </td>

              {/* Remark */}
              <td className="py-2 pr-2">
                <input
                  type="text"
                  value={item.remark}
                  onChange={(e) => updateRow(index, "remark", e.target.value)}
                  placeholder="Input text"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
                />
              </td>

              {/* Delete */}
              <td className="py-2 text-center">
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="text-red-500 hover:text-red-700"
                  aria-label="Remove item">
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        type="button"
        onClick={addRow}
        className="mt-3 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
        <Plus size={16} />
        Add item
      </button>
    </div>
  );
}

